import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlass, ArrowRight, Play, DownloadSimple, Table, Rows, BracketsCurly, X, Clock, CaretDown, CheckCircle, Warning, Database, Trash } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function formatMs(ms) { return ms < 1000 ? ms + 'ms' : (ms / 1000).toFixed(1) + 's'; }
function formatTs(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

var STATUS_COLORS = {
  plan_ready: '#C8A030',
  executing: '#0ABFA3',
  complete: '#1D9E75',
  partial: '#C8A030',
  failed: '#E24B4A',
  blocked: '#3D5A70',
};

/* ── Result Table ── */
function ResultTable({ rows, columns }) {
  if (!rows || rows.length === 0) return <p className="text-xs text-kova-slate font-mono p-4">No results</p>;
  var cols = columns && columns.length > 0 ? columns : (rows[0] && typeof rows[0] === 'object' ? Object.keys(rows[0]).map(function (k) { return { name: k }; }) : []);

  return (
    <div className="overflow-auto max-h-[400px]" data-testid="result-table">
      <table className="w-full text-xs font-mono">
        <thead className="sticky top-0 bg-kova-card border-b border-kova-cyan/12">
          <tr>
            {cols.map(function (c) {
              return <th key={c.name} className="text-left px-3 py-2 text-kova-slate uppercase tracking-wider text-[10px]">{c.name}</th>;
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-kova-cyan/6">
          {rows.slice(0, 200).map(function (row, ri) {
            return (
              <tr key={ri} className="hover:bg-kova-cyan/4 transition-colors">
                {cols.map(function (c) {
                  var val = row[c.name];
                  var display = val === null || val === undefined ? '\u2014' : typeof val === 'object' ? JSON.stringify(val) : String(val);
                  return <td key={c.name} className="px-3 py-1.5 text-kova-silv max-w-[200px] truncate">{display}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Plan Confirmation Card ── */
function PlanCard({ query, onConfirm, onCancel }) {
  var plan = query.execution_plan;
  if (!plan) return null;

  if (plan.is_write_intent) {
    return (
      <div className="card-brutal p-4 border-l-2 border-kova-red">
        <div className="flex items-center gap-2 mb-2">
          <Warning size={16} className="text-kova-red" weight="fill" />
          <span className="text-xs font-mono text-kova-red uppercase tracking-wider">Read-Only Queries Only</span>
        </div>
        <p className="text-sm text-kova-silv">{plan.message || 'Write operations are not supported.'}</p>
      </div>
    );
  }

  if (plan.needs_clarification) {
    return (
      <div className="card-brutal p-4 border-l-2 border-kova-gold">
        <div className="flex items-center gap-2 mb-2">
          <Warning size={16} className="text-kova-gold" />
          <span className="text-xs font-mono text-kova-gold uppercase tracking-wider">Clarification needed</span>
        </div>
        <p className="text-sm text-kova-silv">{plan.clarification_question || 'Please clarify your query.'}</p>
      </div>
    );
  }

  var sources = plan.sources || [];
  return (
    <div className="card-brutal overflow-hidden" data-testid="plan-confirmation-card">
      <div className="px-4 py-3 bg-kova-card border-b border-kova-cyan/12 flex items-center gap-2">
        <span className="text-[10px] font-mono text-kova-cyan uppercase tracking-wider">Execution plan</span>
        {query.from_cache && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-green/15 text-kova-green border border-kova-green/30 uppercase" data-testid="cache-hit-badge">Cache Hit</span>}
        {query.query_source_type === 'historical' && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-purple/15 text-kova-purple border border-kova-purple/30 uppercase" data-testid="source-badge-historical">Historical</span>}
        {query.query_source_type === 'hybrid' && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-gold/15 text-kova-gold border border-kova-gold/30 uppercase" data-testid="source-badge-hybrid">Hybrid</span>}
        {query.query_source_type === 'live' && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-cyan/15 text-kova-cyan border border-kova-cyan/30 uppercase" data-testid="source-badge-live">Live</span>}
        {sources.length > 1 && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-gold/15 text-kova-gold border border-kova-gold/30 uppercase">Multi-Source</span>}
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-kova-silv">{plan.intent || 'Execute query'}</p>
        <div className="space-y-1.5">
          {sources.map(function (s, i) {
            return (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-kova-bg border border-kova-cyan/12">
                <span className="text-[10px] font-mono font-bold text-kova-green">GET</span>
                <span className="text-[11px] font-mono text-kova-silv">{s.connection_name || s.connection_id}</span>
                <span className="text-[9px] font-mono text-kova-slate ml-auto">{s.endpoint}</span>
              </div>
            );
          })}
        </div>
        {plan.join_strategy && plan.join_strategy !== 'none' && (
          <div className="text-[10px] font-mono text-kova-slate">Join: {plan.join_strategy}{plan.join_key ? ' on ' + plan.join_key : ''}</div>
        )}
        <div className="flex gap-2 pt-2">
          <Button onClick={onConfirm} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[10px] tracking-wider uppercase" data-testid="execute-plan-btn">
            <Play size={12} weight="fill" className="mr-1" />Execute
          </Button>
          <Button onClick={onCancel} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[10px] tracking-wider uppercase" data-testid="cancel-plan-btn">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

/* ── Result Card ── */
function ResultCard({ query, onExport }) {
  var [viewMode, setViewMode] = useState('table');
  var result = query.result;
  if (!result) return null;

  // Detect time-series data (period+count columns from historical aggregation)
  var isTimeSeries = result.rows && result.rows.length > 1 && result.rows[0] && result.rows[0].period !== undefined && result.rows[0].count !== undefined;

  var modeBtn = function (mode, icon, label) {
    var active = viewMode === mode;
    return (
      <button key={mode} onClick={function () { setViewMode(mode); }} className={'flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ' + (active ? 'bg-kova-cyan/15 text-kova-cyan border border-kova-cyan/30' : 'text-kova-slate border border-kova-cyan/12 hover:text-kova-silv')} data-testid={'view-mode-' + mode}>{icon}{label}</button>
    );
  };

  return (
    <div className="card-brutal overflow-hidden" data-testid="query-result-card">
      <div className="px-4 py-3 bg-kova-card border-b border-kova-cyan/12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle size={14} className="text-kova-green" weight="fill" />
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">{result.total_count} result{result.total_count !== 1 ? 's' : ''}</span>
          <span className="text-[10px] font-mono text-kova-slate">{formatMs(query.execution_ms)}</span>
          {result.truncated && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-gold/15 text-kova-gold border border-kova-gold/30 uppercase">Truncated to 1000</span>}
          {result.query_source_type === 'historical' && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-purple/15 text-kova-purple border border-kova-purple/30 uppercase" data-testid="result-source-historical">Historical</span>}
          {result.query_source_type === 'hybrid' && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-gold/15 text-kova-gold border border-kova-gold/30 uppercase" data-testid="result-source-hybrid">Hybrid</span>}
          {result.query_source_type === 'live' && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-cyan/15 text-kova-cyan border border-kova-cyan/30 uppercase" data-testid="result-source-live">Live</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {modeBtn('table', <Table size={10} />, 'Table')}
            {modeBtn('summary', <Rows size={10} />, 'Summary')}
            {modeBtn('json', <BracketsCurly size={10} />, 'JSON')}
            {isTimeSeries && modeBtn('chart', <Clock size={10} />, 'Chart')}
          </div>
          <div className="flex gap-1 ml-2">
            <Button onClick={function () { onExport('csv'); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] h-6 px-2" data-testid="export-csv-btn"><DownloadSimple size={10} className="mr-1" />CSV</Button>
            <Button onClick={function () { onExport('json'); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] h-6 px-2" data-testid="export-json-btn"><DownloadSimple size={10} className="mr-1" />JSON</Button>
          </div>
        </div>
      </div>
      <div>
        {viewMode === 'table' && <ResultTable rows={result.rows} columns={result.columns} />}
        {viewMode === 'summary' && (
          <div className="p-4">
            <p className="text-sm text-kova-silv leading-relaxed">{result.summary_text || 'No summary available'}</p>
          </div>
        )}
        {viewMode === 'json' && (
          <div className="p-4 overflow-auto max-h-[400px]">
            <pre className="text-[10px] font-mono text-kova-silv whitespace-pre-wrap">{JSON.stringify(result.rows?.slice(0, 50), null, 2)}</pre>
          </div>
        )}
        {viewMode === 'chart' && isTimeSeries && (
          <div className="p-4" data-testid="time-series-chart">
            <div className="flex items-end gap-1 h-[200px]">
              {(function () {
                var maxCount = Math.max.apply(null, result.rows.map(function (r) { return r.count; }));
                return result.rows.map(function (r, i) {
                  var h = maxCount > 0 ? Math.max(4, (r.count / maxCount) * 180) : 4;
                  return (
                    <div key={i} className="flex flex-col items-center flex-1 min-w-0 group" title={r.period + ': ' + r.count}>
                      <div className="text-[7px] font-mono text-kova-cyan opacity-0 group-hover:opacity-100 transition-opacity mb-1">{r.count}</div>
                      <div className="w-full bg-kova-cyan rounded-t-sm transition-all hover:bg-kova-cyan/80" style={{ height: h + 'px' }} />
                      <div className="text-[6px] font-mono text-kova-slate mt-1 truncate w-full text-center">{r.period.length > 7 ? r.period.slice(5) : r.period}</div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════
   MAIN QUERY ENGINE PAGE
   ═══════════════════════════════ */
export default function QueryEngine() {
  var [queryText, setQueryText] = useState('');
  var [loading, setLoading] = useState(false);
  var [executing, setExecuting] = useState(false);
  var [currentQuery, setCurrentQuery] = useState(null);
  var [history, setHistory] = useState([]);
  var [showHistory, setShowHistory] = useState(false);
  var [preferredModel, setPreferredModel] = useState('anthropic');
  var [suggestions, setSuggestions] = useState([]);
  var [sugLoading, setSugLoading] = useState(false);
  var [cacheStats, setCacheStats] = useState(null);
  var inputRef = useRef(null);

  var fetchHistory = useCallback(function () {
    fetch(API + '/query/history/list?limit=20', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(function (data) { setHistory(data); })
      .catch(function () {});
  }, []);

  var fetchSuggestions = useCallback(function () {
    setSugLoading(true);
    fetch(API + '/query/suggestions', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : { suggestions: [] }; })
      .then(function (data) { setSuggestions(data.suggestions || []); })
      .catch(function () {})
      .finally(function () { setSugLoading(false); });
  }, []);

  var fetchCacheStats = useCallback(function () {
    fetch(API + '/query/cache/stats', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(setCacheStats)
      .catch(function () {});
  }, []);

  useEffect(function () { fetchHistory(); fetchSuggestions(); fetchCacheStats(); }, [fetchHistory, fetchSuggestions, fetchCacheStats]);

  function submitQuery() {
    if (!queryText.trim() || loading) return;
    setLoading(true);
    setCurrentQuery(null);
    fetch(API + '/query/plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ query: queryText.trim(), preferred_model: preferredModel }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw new Error(e.detail || 'Failed'); });
        return res.json();
      })
      .then(function (data) { setCurrentQuery(data); fetchHistory(); })
      .catch(function (e) { toast.error(e.message); })
      .finally(function () { setLoading(false); });
  }

  function executeQuery() {
    if (!currentQuery || executing) return;
    setExecuting(true);
    fetch(API + '/query/' + currentQuery.query_id + '/execute', {
      method: 'POST', credentials: 'include',
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw new Error(e.detail || 'Execution failed'); });
        return res.json();
      })
      .then(function (data) { setCurrentQuery(data); fetchHistory(); toast.success('Query executed'); })
      .catch(function (e) { toast.error(e.message); })
      .finally(function () { setExecuting(false); });
  }

  function exportResult(fmt) {
    if (!currentQuery) return;
    window.open(API + '/query/' + currentQuery.query_id + '/export?fmt=' + fmt, '_blank');
  }

  function loadFromHistory(item) {
    fetch(API + '/query/' + item.query_id, { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) { setCurrentQuery(data); setQueryText(data.raw_query || ''); setShowHistory(false); }
      })
      .catch(function () {});
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitQuery(); }
  }

  function handleSuggestionClick(suggestion) {
    setQueryText(suggestion.text);
    // Track for MATR
    fetch(API + '/query/suggestions/click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ suggestion: suggestion }),
    }).catch(function () {});
    // Auto-execute
    setLoading(true);
    setCurrentQuery(null);
    fetch(API + '/query/plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ query: suggestion.text, preferred_model: preferredModel }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw new Error(e.detail || 'Failed'); });
        return res.json();
      })
      .then(function (data) { setCurrentQuery(data); fetchHistory(); })
      .catch(function (e) { toast.error(e.message); })
      .finally(function () { setLoading(false); });
  }

  var modelOptions = [
    { value: 'anthropic', label: 'Claude', color: '#D4A574' },
    { value: 'openai', label: 'GPT', color: '#00A67E' },
    { value: 'gemini', label: 'Gemini', color: '#4285F4' },
  ];

  return (
    <div className="space-y-4" data-testid="query-engine-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-lg">Query Engine</h1>
          <p className="text-[10px] font-mono text-kova-slate mt-0.5">Read-only cross-system queries in natural language</p>
        </div>
        {cacheStats && (
          <div className="flex items-center gap-3" data-testid="cache-stats">
            <div className="flex items-center gap-1.5 text-[9px] font-mono text-kova-slate">
              <Database size={10} className="text-kova-cyan" />
              <span>{cacheStats.active} cached</span>
              <span className="text-kova-cyan/12">|</span>
              <span>{cacheStats.total_hits} hits</span>
            </div>
            {cacheStats.active > 0 && (
              <button onClick={function () {
                fetch(API + '/query/cache', { method: 'DELETE', credentials: 'include' })
                  .then(function (r) { if (r.ok) { fetchCacheStats(); toast.success('Cache cleared'); } });
              }} className="text-[9px] font-mono text-kova-slate hover:text-kova-red transition-colors flex items-center gap-0.5" data-testid="clear-cache-btn">
                <Trash size={10} />Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Query Input */}
      <div className="card-brutal p-4 space-y-3">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-kova-slate" />
          <textarea
            ref={inputRef}
            value={queryText}
            onChange={function (e) { setQueryText(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your connected APIs..."
            className="w-full bg-kova-bg border border-kova-cyan/12 text-sm text-white placeholder:text-kova-slate pl-10 pr-4 py-2.5 resize-none focus:outline-none focus:border-kova-cyan/50 font-mono min-h-[44px] max-h-[120px]"
            rows={1}
            data-testid="query-input"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-mono text-kova-slate uppercase tracking-wider">Model:</span>
            {modelOptions.map(function (opt) {
              var active = preferredModel === opt.value;
              return (
                <button key={opt.value} onClick={function () { setPreferredModel(opt.value); }} className={'text-[10px] font-mono px-2 py-0.5 border transition-colors ' + (active ? 'border-kova-cyan/30 text-kova-cyan bg-kova-cyan/10' : 'border-kova-cyan/12 text-kova-slate hover:text-kova-silv')} data-testid={'model-' + opt.value}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={function () { setShowHistory(!showHistory); }} className="text-[10px] font-mono text-kova-slate hover:text-kova-silv flex items-center gap-1 transition-colors" data-testid="toggle-history-btn">
              <Clock size={12} />History
              <CaretDown size={10} className={'transition-transform ' + (showHistory ? 'rotate-180' : '')} />
            </button>
            <Button onClick={submitQuery} disabled={!queryText.trim() || loading} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[10px] tracking-wider uppercase" data-testid="submit-query-btn">
              {loading ? 'Planning...' : <><ArrowRight size={12} weight="bold" className="mr-1" />Query</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && !currentQuery && (
        <div className="space-y-2" data-testid="query-suggestions">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px]">Suggested queries</span>
            <button onClick={fetchSuggestions} className="text-[9px] font-mono text-kova-slate hover:text-kova-cyan transition-colors" data-testid="refresh-suggestions-btn">{sugLoading ? '...' : 'Refresh'}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map(function (sug) {
              var isCross = sug.type === 'cross_system';
              return (
                <button
                  key={sug.suggestion_id}
                  onClick={function () { handleSuggestionClick(sug); }}
                  className={'group text-left px-3 py-2 border transition-all hover:border-kova-cyan/40 hover:bg-kova-cyan/5 max-w-[340px] ' + (isCross ? 'border-kova-cyan/20 bg-kova-cyan/[0.03]' : 'border-kova-cyan/12')}
                  data-testid={'suggestion-' + sug.suggestion_id}
                >
                  <div className="flex items-start gap-2">
                    {isCross && <span className="shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full bg-kova-cyan" />}
                    <span className="text-[11px] text-kova-silv group-hover:text-white transition-colors leading-snug">{sug.text}</span>
                  </div>
                  {sug.providers && sug.providers.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {sug.providers.map(function (p) {
                        return <span key={p} className="text-[8px] font-mono px-1.5 py-0.5 bg-white/4 text-kova-slate uppercase tracking-wider">{p}</span>;
                      })}
                      {isCross && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-cyan/10 text-kova-cyan/70 uppercase tracking-wider">cross-system</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* History dropdown */}
      <AnimatePresence>
        {showHistory && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="card-brutal divide-y divide-kova-cyan/12 max-h-[250px] overflow-y-auto" data-testid="query-history">
              {history.length === 0 ? (
                <div className="p-4 text-center text-xs text-kova-slate font-mono">No queries yet</div>
              ) : history.map(function (h) {
                return (
                  <button key={h.query_id} onClick={function () { loadFromHistory(h); }} className="w-full text-left px-4 py-2.5 hover:bg-kova-card/50 transition-colors" data-testid={'history-' + h.query_id}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-kova-silv truncate flex-1">{h.raw_query}</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.5 uppercase" style={{ color: STATUS_COLORS[h.status] || '#3D5A70' }}>{h.status}</span>
                      <span className="text-[9px] font-mono text-kova-slate">{h.row_count || 0} rows</span>
                    </div>
                    <div className="text-[9px] font-mono text-kova-slate mt-0.5">{formatTs(h.created_at)} · {h.model_used} · {formatMs(h.execution_ms)}</div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Current query state */}
      <AnimatePresence mode="wait">
        {loading && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="card-brutal p-6 text-center">
            <div className="inline-block w-5 h-5 border-2 border-kova-cyan/30 border-t-kova-cyan rounded-full animate-spin mb-2" />
            <p className="text-xs font-mono text-kova-slate">Generating execution plan...</p>
          </motion.div>
        )}
        {executing && (
          <motion.div key="executing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="card-brutal p-6 text-center">
            <div className="inline-block w-5 h-5 border-2 border-kova-cyan/30 border-t-kova-cyan rounded-full animate-spin mb-2" />
            <p className="text-xs font-mono text-kova-slate">Executing query across sources...</p>
          </motion.div>
        )}
        {!loading && !executing && currentQuery && currentQuery.status === 'plan_ready' && (
          <motion.div key="plan" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <PlanCard query={currentQuery} onConfirm={executeQuery} onCancel={function () { setCurrentQuery(null); }} />
          </motion.div>
        )}
        {!loading && !executing && currentQuery && currentQuery.status === 'blocked' && (
          <motion.div key="blocked" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <PlanCard query={currentQuery} onConfirm={function () {}} onCancel={function () { setCurrentQuery(null); }} />
          </motion.div>
        )}
        {!loading && !executing && currentQuery && (currentQuery.status === 'complete' || currentQuery.status === 'partial') && currentQuery.result && (
          <motion.div key="result" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-3">
            {currentQuery.error && (
              <div className="card-brutal p-3 border-l-2 border-kova-gold">
                <p className="text-xs text-kova-gold font-mono">{currentQuery.error}</p>
              </div>
            )}
            <ResultCard query={currentQuery} onExport={exportResult} />
          </motion.div>
        )}
        {!loading && !executing && currentQuery && currentQuery.status === 'failed' && (
          <motion.div key="failed" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="card-brutal p-4 border-l-2 border-kova-red">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={16} className="text-kova-red" weight="fill" />
              <span className="text-xs font-mono text-kova-red uppercase tracking-wider">Query Failed</span>
            </div>
            <p className="text-sm text-kova-silv">{currentQuery.error || 'Unknown error'}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
